import React, { Dispatch, SetStateAction } from 'react';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import { X } from 'lucide-react';
import { UploadContainer } from '@/components/common/upload';
import clsx from 'clsx';
import { useNotification } from '@/hooks';
import { logApiError } from '@/lib/logApiError';

interface Props {
	isOpen: boolean;
	customFileType?: string;
	title?: string;
	pdfFile: File | undefined;
	customSuccessToast?: string;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	setPdfFile: (value: File | undefined) => void;
	handleConfirmButton?: () => void;
}

export const UploadPDFModal: React.FC<Props> = ({
	isOpen,
	customFileType,
	title,
	customSuccessToast,
	setIsModalOpen,
	setPdfFile,
	pdfFile,
	handleConfirmButton,
}) => {
	const url = window.location.href;
	const { Modal, show, hide } = useModal();
	const [fileError, setFileError] = React.useState<string>('');
	const [uploadProgress, setUploadProgress] = React.useState<number>(0);
	const [uploadingDocument, setUploadingDocument] =
		React.useState<boolean>(false);
	const { toastSuccess, toastError } = useNotification();

	React.useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	const handleDropFile = (e: React.DragEvent<HTMLDivElement>): void => {
		e.preventDefault();
		e.stopPropagation();
		const droppedFile = e.dataTransfer.files[0];
		handleUploadFile(droppedFile);
	};

	const handleCloseModal = (): void => {
		setFileError('');
		hide();
		setIsModalOpen(false);
	};

	const handleUploadFile = async (file: File): Promise<void> => {
		if (!file) return;
		setFileError('');
		const maxFileSizeInMB = 25;
		if (file.size > maxFileSizeInMB * 1024 * 1024) {
			setFileError(
				'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.'
			);
			logApiError({
				status: 400,
				message:
					'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.',
				config: { data: null, url: url },
			});
			return;
		}

		if (file.type !== 'application/pdf') {
			setFileError(
				'Error Upload failed: Ensure the document is in PDF format, and under 25 MB.'
			);
			logApiError({
				status: 400,
				message:
					'Error Upload failed: Ensure the document is in PDF format, and under 25 MB.',
				config: { data: null, url: url },
			});
			return;
		}

		try {
			setUploadingDocument(true);
			setUploadProgress(0);

			// Simulate the upload process
			await new Promise<void>((resolve) => {
				const totalSteps = 100;
				let currentProgress = 0;

				const intervalId = setInterval(() => {
					currentProgress += 1;
					setUploadProgress(currentProgress);

					if (currentProgress >= totalSteps) {
						clearInterval(intervalId);
						resolve();
					}
				}, 500 / totalSteps);
			});

			setPdfFile(file);
			setFileError('');
			toastSuccess(
				customSuccessToast || 'The document has been successfully uploaded.'
			);
		} catch (error: any) {
			toastError('Error: The file could not be uploaded. Please try again.');
		} finally {
			setUploadingDocument(false);
			setUploadProgress(0); // Reset progress after completion
		}
	};

	return (
		<Modal classNameCardContainer="!bg-gradient-to-r from-white to-white sm:!w-[532px]">
			<div className="w-full flex flex-col items-center space-y-6">
				<div id="header" className="space-y-3 w-full relative">
					<div className="flex items-center justify-center gap-[18px]">
						<Icon src={Icons.imageCardsAlt} className="!w-16" />
						<Typography type="semibold-20">
							{title || 'Upload Document'}
						</Typography>
						<button
							className="absolute right-0 top-2"
							onClick={handleCloseModal}
						>
							<X />
						</button>
					</div>
				</div>
				<div
					id="content"
					className={clsx(
						'rounded-[32px] border border-dashed  bg-gray-20 w-full',
						{
							'border-main-red': fileError,
							'border-opacity-primary': !fileError,
						}
					)}
					onDrop={handleDropFile}
					onDragOver={(e) => e.preventDefault()}
				>
					<div className="py-6 px-8 flex flex-col items-center">
						<div className="mb-8">
							<Typography type="regular-14" className="opacity-60 text-center">
								Drag and drop {customFileType ? `your ${customFileType}` : ''}{' '}
								here,
							</Typography>
							<Typography type="regular-14" className="opacity-60 text-center">
								or click to browse from your device.
							</Typography>
							<Typography type="regular-14" className="opacity-60 text-center">
								Accepted formats: PDF.
							</Typography>
						</div>
						{!pdfFile && !uploadingDocument && (
							<UploadContainer
								id="action-required"
								setFile={handleUploadFile}
								accept=".pdf"
							>
								<div className="flex cursor-pointer items-center bg-primary rounded-full text-base font-semibold text-white gap-2 px-6 h-[30px] py-4">
									Upload
									<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
								</div>
							</UploadContainer>
						)}

						{uploadingDocument && (
							<>
								<div className="flex items-centers px-3 py-2 bg-gray-5 rounded-xl gap-2 w-[206px]">
									<Icon
										src={Icons.documentUploadThin}
										className="!w-6 !h-6 !text-primary"
									/>
									<div className="flex items-center w-full">
										<div className="h-2 bg-white rounded w-full">
											<div
												className="h-2 bg-main-blue rounded"
												style={{ width: `${uploadProgress}%` }}
											/>
										</div>
									</div>
									<div className="flex items-center">
										<Icon
											src={Icons.close}
											className="!w-4 !h-4 cursor-pointer"
											onClick={() => {
												setUploadingDocument(false);
												setPdfFile(undefined);
											}}
										/>
									</div>
								</div>
							</>
						)}
						{pdfFile && (
							<UploadFileCard pdfName={pdfFile.name} setPdfFile={setPdfFile} />
						)}
						{fileError && (
							<div className="mt-6">
								<Typography
									type="regular-12"
									className="text-center text-main-red"
								>
									{(() => {
										// Find the index of the first dot or comma
										const splitIndex = fileError.search(/[.,]/);

										if (splitIndex !== -1) {
											// Split the string into two parts
											const firstPart = fileError.slice(0, splitIndex + 1); // Include the dot/comma
											const secondPart = fileError.slice(splitIndex + 1).trim(); // Start after the dot/comma
											return (
												<>
													{firstPart}
													<br />
													{secondPart}
												</>
											);
										}
										// If no dot or comma, return the whole string as-is
										return fileError;
									})()}
								</Typography>
							</div>
						)}
					</div>
				</div>

				{handleConfirmButton && (
					<div className="flex gap-2 items-center">
						<Button
							label="Cancel"
							decoration="secondary"
							onClick={() => {
								setUploadingDocument(false);
								setPdfFile(undefined);
								setIsModalOpen(false);
								handleCloseModal();
							}}
						/>
						<Button
							label="Confirm"
							decoration="primary"
							onClick={() => {
								handleConfirmButton?.();
								handleCloseModal();
							}}
							disabled={!pdfFile}
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};

export const UploadFileCard: React.FC<{
	pdfName: string;
	setPdfFile: (value: File | undefined) => void;
	removeIconRemove?: string;
	classNameIcon?: string;
	handleRemove?: () => void;
	disabled?: boolean;
}> = ({
	pdfName,
	setPdfFile,
	removeIconRemove,
	classNameIcon,
	disabled,
	handleRemove,
}) => {
	return (
		<div className="flex items-centers px-3 py-2 bg-gray-5 rounded-xl gap-2">
			<div className="flex items-center gap-2 max-w-[180px]">
				<Icon
					src={Icons.documentUploadThin}
					className="!w-6 !h-6 !text-primary"
				/>
				<Typography
					type="regular-10"
					className="opacity-60 text-center truncate"
				>
					{pdfName}
				</Typography>
			</div>
			{!disabled && (
				<button
					type="button"
					className="flex items-center"
					onClick={() => {
						setPdfFile(undefined);
						handleRemove?.();
					}}
				>
					<Icon
						src={removeIconRemove || Icons.close}
						className={clsx('!w-4 !h-4 cursor-pointer', classNameIcon)}
					/>
				</button>
			)}
		</div>
	);
};
